import { useEffect, useSyncExternalStore } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { ApiError, type NodeInfo } from '../../api'
import {
  POLL_MS,
  RELAY,
  RELAY_EVENTS_PATH,
  RELAY_EVENT_NAMES,
  getAsOf,
  type RelayJournalEvent,
  type RelayLink,
  type RelayList,
  type NoPathStream,
  type RelayNodeEvent,
  type RelayRoute,
  type RelaySettings,
  type RelaySnapshot,
} from './api'
import { noteServerTimeEvent, toCenter } from './clock'
import { subscribeSharedSse, type SharedSseInfo, type SharedSseState } from '../../sharedSse'

/*
 * Live data of the network console: the SSE stream /api/admin/relay/events
 * (04-admin-api.md §7; docs/controlapi.yaml). One connection per tab, shared
 * by every admin page that shows relay data, opened while one is mounted.
 *
 * - On connect the center sends a snapshot (or the missed events when the
 *   browser's own reconnect sends Last-Event-ID), then route / link /
 *   node_relay / journal events.
 * - The console tabs of this browser share the stream (sharedSse.ts): one tab
 *   holds it and passes the events on; a tab that joins gets the current
 *   routes, links, nodes and journal as a snapshot from it.
 * - The browser reconnects by itself (retry: 2000). If the stream stays down
 *   for LOST_AFTER_MS, or the server refuses it, the state is "lost": the
 *   lists are polled every 5 s and the console says so; the shared stream
 *   tries again (2 s growing to 30 s), and once it opens polling stops and
 *   the relay queries are refetched.
 * - Freshness: every answer and event carries as_of (center time the data
 *   was true) and the center's clock (X-Server-Time / server_time, decision 37):
 *   ages are measured on the center's clock (clock.ts), not the browser's. While the stream is open the data is as fresh as the newest
 *   as_of was on arrival; without the stream it ages with the clock. Older
 *   than 15 s → the banner (STALE_MS).
 * - The center limits the streams (decision 51) and refuses one more with 429 +
 *   Retry-After. EventSource only sees a closed stream, so a refusal is asked
 *   once with fetch (sse.ts): 429 → "limited": polling as when lost, the banner
 *   says LIVE UPDATES LIMITED and the stream is tried again after Retry-After.
 * - A center without the relay API (GET /relay/settings → 404) gets no
 *   stream, no banner and no relay elements: `available` is false.
 */

/** limited: the center refused the stream by its limit (429, decision 51): polling until Retry-After */
export type SseState = 'off' | 'connecting' | 'open' | 'reconnecting' | 'lost' | 'limited'

export interface RelayLive {
  /** null until GET /relay/settings answered; false on 404 (center without the relay API) */
  available: boolean | null
  settings?: RelaySettings
  sse: SseState
  /** live routes by id (ended ones leave) */
  routes: ReadonlyMap<string, RelayRoute & { as_of?: string }>
  nodes: ReadonlyMap<string, RelayNodeEvent>
  links: ReadonlyMap<string, RelayLink>
  /** streams without a relay route (SSE no_path / no_path_removed, snapshot.no_path) */
  noPath: ReadonlyMap<string, NoPathStream>
  /** journal events seen on the stream, newest first */
  journal: readonly RelayJournalEvent[]
  /** newest center time of the data (ms), null before any */
  asOf: number | null
  /** center time that newest data arrived */
  at: number
  /** the last request to the center failed (poll, settings, a card's GET) */
  failing: boolean
  /** id of the last SSE event (boot-n) */
  lastEventId: string
  /** routes / nodes were seen at least once (snapshot or poll) */
  loaded: boolean
  /** limited: when (browser ms) the stream is tried again */
  retryAt?: number
}

/** Stream down this long (browser retrying) → lost: poll instead. */
export const LOST_AFTER_MS = 6000
/** Kept events of the journal. */
const JOURNAL_MAX = 200

const EMPTY: RelayLive = {
  available: null,
  sse: 'off',
  routes: new Map(),
  nodes: new Map(),
  links: new Map(),
  noPath: new Map(),
  journal: [],
  asOf: null,
  at: 0,
  failing: false,
  lastEventId: '',
  loaded: false,
}

type Listener = () => void

class RelayLiveStore {
  private state: RelayLive = EMPTY
  private listeners = new Set<Listener>()
  private refs = 0
  private stopTimer: ReturnType<typeof setTimeout> | undefined
  /** leaves the shared stream */
  private leave: (() => void) | null = null
  private lostTimer: ReturnType<typeof setTimeout> | undefined
  private pollTimer: ReturnType<typeof setInterval> | undefined
  private running = false
  private qc: QueryClient | null = null

  get = () => this.state

  subscribe = (l: Listener) => {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }

  private set(patch: Partial<RelayLive>) {
    this.state = { ...this.state, ...patch }
    for (const l of this.listeners) l()
  }

  /** A page that shows relay data mounted. */
  retain(qc: QueryClient) {
    this.qc = qc
    this.refs++
    clearTimeout(this.stopTimer)
    if (!this.running) void this.start()
  }

  /** …and unmounted: stop a moment later unless another page takes over. */
  release() {
    this.refs = Math.max(0, this.refs - 1)
    if (this.refs === 0) this.stopTimer = setTimeout(() => this.stop(), 2000)
  }

  /** as_of of an answer a page fetched itself (route card, nodes). */
  note(asOf: number, at: number) {
    if (this.state.asOf === null || asOf > this.state.asOf) this.set({ asOf, at, failing: false })
    else if (this.state.failing) this.set({ failing: false })
  }

  /** A page's own request to the center failed (not 4xx). */
  noteFailure() {
    if (!this.state.failing) this.set({ failing: true })
  }

  private async start() {
    this.running = true
    try {
      const s = await getAsOf<RelaySettings>(`${RELAY}/settings`)
      if (!this.running) return
      this.set({ available: true, settings: s.data })
      this.note(s.asOf, s.at)
    } catch (e) {
      if (!this.running) return
      if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
        this.set({ available: false })
        this.running = false
        return
      }
      // the center did not answer: assume the API is there and let the stream decide
      this.set({ available: true, failing: true })
    }
    this.connect()
  }

  private stop() {
    this.running = false
    this.leave?.()
    this.leave = null
    clearTimeout(this.lostTimer)
    clearInterval(this.pollTimer)
    this.pollTimer = undefined
    this.state = { ...EMPTY }
    for (const l of this.listeners) l()
  }

  private connect() {
    if (!this.running || this.leave || typeof EventSource === 'undefined') return
    this.leave = subscribeSharedSse({
      path: RELAY_EVENTS_PATH,
      key: 'strevix-live-relay',
      events: RELAY_EVENT_NAMES,
      onEvent: (e) => {
        if (!this.running) return
        let data: unknown
        try {
          data = JSON.parse(e.data)
        } catch {
          return
        }
        this.apply(e.name, data, e.lastEventId ?? '')
      },
      onState: (st, info) => this.onStream(st, info),
      snapshot: () => this.snapshotEvent(),
    })
  }

  private onStream(st: SharedSseState, info: SharedSseInfo) {
    if (!this.running) return
    const lost = this.state.sse === 'lost' || this.state.sse === 'limited'
    switch (st) {
      case 'connecting':
        if (!lost) this.set({ sse: 'connecting' })
        this.armLost()
        return
      case 'open':
        clearTimeout(this.lostTimer)
        this.stopPolling()
        this.set({ sse: 'open', failing: false, retryAt: undefined })
        if (lost) void this.qc?.invalidateQueries({ queryKey: ['relay'] })
        return
      case 'reconnecting':
        // refused by another answer than 429: lost at once; a drop: lost after LOST_AFTER_MS
        if (info.refused || this.state.sse === 'limited') this.markLost()
        else if (this.state.sse === 'open') {
          this.set({ sse: 'reconnecting' })
          this.armLost()
        }
        // still connecting: the timer armed then runs on
        return
      case 'limited':
        this.markLimited(info.retryAt ?? Date.now())
        return
      case 'paused':
        // every console tab is hidden: nobody looks, no polling either
        clearTimeout(this.lostTimer)
        this.stopPolling()
        this.set({ sse: 'connecting', retryAt: undefined })
        return
    }
  }

  /** What a console tab that joins the shared stream gets from this one. */
  private snapshotEvent() {
    const s = this.state
    if (!s.loaded) return null
    const snap: RelaySnapshot & { journal: readonly RelayJournalEvent[] } = {
      as_of: new Date(s.asOf ?? toCenter(Date.now())).toISOString(),
      routes: [...s.routes.values()],
      links: [...s.links.values()],
      nodes: [...s.nodes.values()],
      no_path: [...s.noPath.values()],
      journal: s.journal,
    }
    return { name: 'snapshot', data: JSON.stringify(snap), lastEventId: s.lastEventId || undefined }
  }

  private armLost() {
    clearTimeout(this.lostTimer)
    this.lostTimer = setTimeout(() => {
      if (this.state.sse !== 'open') this.markLost()
    }, LOST_AFTER_MS)
  }

  /** Refused by the limit (429): polling until the shared stream tries again at retryAt. */
  private markLimited(retryAt: number) {
    if (!this.running) return
    clearTimeout(this.lostTimer)
    this.set({ sse: 'limited', retryAt })
    this.startPolling()
  }

  private markLost() {
    if (!this.running) return
    clearTimeout(this.lostTimer)
    if (this.state.sse !== 'lost') this.set({ sse: 'lost', retryAt: undefined })
    this.startPolling()
  }

  private startPolling() {
    if (this.pollTimer) return
    void this.poll()
    this.pollTimer = setInterval(() => void this.poll(), POLL_MS)
  }

  private stopPolling() {
    clearInterval(this.pollTimer)
    this.pollTimer = undefined
  }

  /** While lost: the live routes and the nodes (relay state, no_path), and the open card refetches. */
  private async poll() {
    try {
      const [routes, nodes] = await Promise.all([
        getAsOf<RelayList<RelayRoute>>(`${RELAY}/routes?limit=500`),
        getAsOf<NodeInfo[]>('/api/admin/nodes'),
      ])
      if (!this.running || this.state.sse === 'open') return
      const nm = new Map<string, RelayNodeEvent>()
      for (const n of nodes.data) nm.set(n.id, { id: n.id, status: n.status, relay: n.relay, load: n.load, clock: n.clock })
      this.set({ routes: new Map(routes.data.items.filter((r) => r.state !== 'ended').map((r) => [r.route_id, r])), nodes: nm, loaded: true })
      this.note(Math.max(routes.asOf, nodes.asOf), Math.max(routes.at, nodes.at))
      void this.qc?.invalidateQueries({ queryKey: ['relay', 'route'] })
    } catch {
      if (this.running) this.noteFailure()
    }
  }

  private apply(name: string, data: unknown, id: string) {
    const d = data as { as_of?: string; server_time?: string }
    const arrived = Date.now()
    noteServerTimeEvent(d?.server_time, arrived)
    const at = toCenter(arrived)
    const asOf = d?.as_of ? Date.parse(d.as_of) : NaN
    // an event is an answer of the center
    const patch: Partial<RelayLive> = { lastEventId: id || this.state.lastEventId, failing: false }
    switch (name) {
      case 'snapshot': {
        const s = data as RelaySnapshot
        patch.routes = new Map(s.routes.filter((r) => r.state !== 'ended').map((r) => [r.route_id, r]))
        patch.links = new Map(s.links.map((l) => [l.link_id, l]))
        patch.nodes = new Map(s.nodes.map((n) => [n.id, n]))
        patch.noPath = new Map((s.no_path ?? []).map((x) => [x.stream, x]))
        // the snapshot of another console tab (sharedSse.ts) carries its journal too
        const j = (data as { journal?: RelayJournalEvent[] }).journal
        if (Array.isArray(j) && this.state.journal.length === 0) patch.journal = j.slice(0, JOURNAL_MAX)
        patch.loaded = true
        break
      }
      case 'route': {
        const r = data as RelayRoute
        const m = new Map(this.state.routes)
        if (r.state === 'ended') m.delete(r.route_id)
        else m.set(r.route_id, r)
        patch.routes = m
        break
      }
      case 'route_removed': {
        const m = new Map(this.state.routes)
        m.delete((data as { route_id: string }).route_id)
        patch.routes = m
        break
      }
      case 'link': {
        const l = data as RelayLink
        patch.links = new Map(this.state.links).set(l.link_id, l)
        break
      }
      case 'link_removed': {
        const m = new Map(this.state.links)
        m.delete((data as { link_id: string }).link_id)
        patch.links = m
        break
      }
      case 'node_relay': {
        const n = data as RelayNodeEvent
        patch.nodes = new Map(this.state.nodes).set(n.id, n)
        break
      }
      case 'no_path': {
        const x = data as NoPathStream
        patch.noPath = new Map(this.state.noPath).set(x.stream, x)
        break
      }
      case 'no_path_removed': {
        const m = new Map(this.state.noPath)
        m.delete((data as { stream: string }).stream)
        patch.noPath = m
        break
      }
      case 'journal': {
        const e = data as RelayJournalEvent
        if (!this.state.journal.some((x) => x.id === e.id)) patch.journal = [e, ...this.state.journal].slice(0, JOURNAL_MAX)
        break
      }
    }
    if (Number.isFinite(asOf) && (this.state.asOf === null || asOf >= this.state.asOf)) {
      patch.asOf = asOf
      patch.at = at
    }
    this.set(patch)
  }
}

const store = new RelayLiveStore()

/** The live relay data; keeps the stream open while the calling component is mounted. */
export function useRelayLive(): RelayLive {
  const qc = useQueryClient()
  useEffect(() => {
    store.retain(qc)
    return () => store.release()
  }, [qc])
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}

/** For the pages' own fetches: report as_of of an answer, or a failed request. */
export const noteRelayAsOf = (asOf: number, at: number) => store.note(asOf, at)
export const noteRelayFailure = (e: unknown) => {
  if (!(e instanceof ApiError) || e.status >= 500) store.noteFailure()
}

/**
 * Age of the console's data in ms (handoff §5): while the stream is open, how
 * old the newest data was when it arrived; otherwise it grows with the clock.
 * `now` is the browser's clock; everything is compared on the center's.
 */
export function dataAge(l: RelayLive, now: number): number | null {
  if (l.asOf === null) return null
  return l.sse === 'open' ? Math.max(0, l.at - l.asOf) : Math.max(0, toCenter(now) - l.asOf)
}
